import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AssessmentIcon from '@mui/icons-material/Assessment'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import AssignmentIcon from '@mui/icons-material/Assignment'
import EventIcon from '@mui/icons-material/Event'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SaveIcon from '@mui/icons-material/Save'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { STATUS_DEFESA_LABEL, StatusDefesa } from '@/types/resumoAdmin'
import { authStore } from '@/store/authStore'
import { oportunidadeService } from '@/services/oportunidadeService'
import { clienteService } from '@/services/clienteService'
import { documentoService } from '@/services/documentoService'
import { reuniaoService } from '@/services/reuniaoService'
import { Oportunidade } from '@/types/oportunidade'
import { Cliente } from '@/types/cliente'
import { Reuniao } from '@/types/reuniao'
import {
  TIPO_ENTIDADE_OPORTUNIDADE,
  vincularDocumentos,
  DocumentoObrigatorio,
} from '@/utils/docsObrigatorios'
import { DocsObrigatoriosDialog } from '@/components/reservas/DocsObrigatoriosDialog'
import { DocumentosReservaDialog } from '@/components/documentos/DocumentosReservaDialog'
import { ReuniaoDialog } from '@/components/agenda/ReuniaoDialog'
import { SolicitarReuniaoDialog } from '@/components/agenda/SolicitarReuniaoDialog'
import { SeletorEtapaReserva } from '@/components/pipeline/SeletorEtapaReserva'
import {
  MOTIVO_AGENDAMENTO_INDISPONIVEL,
  STATUS_LABEL,
  corDoStatus,
  permiteAgendamento,
  type StatusReserva,
} from '@/types/statusReserva'
import { mensagemErroDe } from '@/components/pipeline/erros'
import { formatCNPJ } from '@/utils/formatters'

/**
 * Esteira de Estruturação do back-office: reservas que a Originei já APROVOU, da aprovação
 * até a operação ativa (entrega F, 16/10/2026 — antes só aprovada/em estruturação/devolvida).
 * Ordem = a da jornada (`ORDEM_STATUS`). Pendentes de aprovação ficam na Validação de Reservas;
 * perdidas/encerradas saem da esteira e seguem no Pipeline Comercial.
 */
export const STATUS_ESTEIRA: StatusReserva[] = [
  'APROVADA_ORIGINEI',
  'EM_ESTRUTURACAO',
  'EM_NEGOCIACAO',
  'EM_ANALISE_PARCEIRO',
  'APROVADA_PARCEIRO',
  'REPROVADA_PARCEIRO',
  'EM_CONTRATACAO',
  'CONTRATO_ASSINADO',
  'EM_OPERACIONALIZACAO',
  'ATIVO',
]

/** Tamanho da carga (filtro por etapa é no cliente, sobre esta carga). */
const TAMANHO_PAGINA = 200

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** O que a busca livre enxerga: número da reserva, empresa, CNPJ, consultor e produto. */
const textoDeBusca = (r: ReservaRow): string => {
  const numero = r.op.numero != null ? `RES-${String(r.op.numero).padStart(4, '0')}` : ''
  const cnpjDigitos = (r.op.cnpj ?? '').replace(/\D/g, '')
  return [numero, r.empresa, r.cnpj, cnpjDigitos, r.consultor ?? '', r.produto].join(' ')
}

/** Mesmo limite do backend (`analiseOriginei`, máx. 20000). */
const ANALISE_MAX = 20000

// ─── Estado visual do card: só pela documentação obrigatória ───────────────
// Documentos não travam a movimentação (o servidor não bloqueia nesta fase; o Kanban
// também não) — o card só sinaliza a pendência.
type EstadoCard =
  | 'DOCS_OK'     // nenhum obrigatório faltando → verde
  | 'AGUARDANDO'  // falta 1                     → warning
  | 'PENDENTE'    // faltam 2+                   → danger, banner vermelho

interface ReservaRow {
  id: string
  empresa: string
  cnpj: string
  /** Nome do consultor gravado na reserva (V49); nulo em reserva antiga sem nome conhecido. */
  consultor: string | null
  produto: string
  valor: number
  /** Score de aderência (0–100) calculado pelo backend — o critério da fila. */
  aderencia: number
  atualizadoEm: string
  op: Oportunidade
  /** Status real (18 estados); nulo só em backend antigo. */
  status: StatusReserva | null
  obrigatoriosFaltantes: number
  /** Nulo = catálogo indisponível e nenhum faltante informado: não dá para afirmar "Sem checklist". */
  obrigatoriosTotal: number | null
  proximaReuniao: Reuniao | null
  estado: EstadoCard
  // ─── Solicitação de reunião (entrega G) — vêm na própria listagem ───
  reuniaoSolicitadaEm: string | null
  reuniaoSolicitadaObservacao: string | null
  /** Reunião realizada (primeiro contato), YYYY-MM-DD. */
  dataPrimeiroContato: string | null
  // ─── Documentação % + Defesa (Onda 6 / A11): vêm na PRÓPRIA listagem
  // (`OportunidadeResumoResponse.statusDefesa` / `percentualChecklist`, lacuna (d) da
  // Rodada 2) — acabou o GET detalhe + GET checklist por reserva do A05.
  /** true quando a listagem trouxe os campos novos — statusDefesa/parceiro são confiáveis. */
  temDetalhe?: boolean
  statusDefesa?: StatusDefesa | null
  /** Percentual do checklist (0–100) calculado no backend; null = backend anterior. */
  checklistPercentual?: number | null
  parceiroDefinido?: boolean
}

/** Data/hora da reunião agendada ("DD/MM/YYYY HH:mm"). */
const formatDataHora = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Data ISO (com ou sem hora) como "DD/MM/YYYY"; data pura sem deslocar o fuso. */
const formatData = (iso: string): string =>
  new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso).toLocaleDateString('pt-BR')

/**
 * Linha "Reunião:" do card. Precedência: realizada (primeiro contato) → agendada →
 * solicitada ao consultor → não solicitada.
 */
const situacaoReuniao = (r: ReservaRow): { texto: string; destaque: boolean } => {
  if (r.dataPrimeiroContato) return { texto: `Realizada em ${formatData(r.dataPrimeiroContato)}`, destaque: true }
  if (r.proximaReuniao?.dataInicio) return { texto: formatDataHora(r.proximaReuniao.dataInicio), destaque: true }
  if (r.reuniaoSolicitadaEm) {
    return { texto: `Solicitada ao consultor em ${formatData(r.reuniaoSolicitadaEm)}`, destaque: false }
  }
  return { texto: 'Não solicitada', destaque: false }
}

const formatBRL = (v?: number | null) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

// Cor da borda esquerda + paleta de chips por estado.
const ESTADO_COLOR: Record<EstadoCard, { border: string; chip: 'success' | 'warning' | 'error' }> = {
  DOCS_OK:    { border: '#2ECC71', chip: 'success' }, // verde
  AGUARDANDO: { border: '#F39C12', chip: 'warning' }, // laranja warn
  PENDENTE:   { border: '#E74C3C', chip: 'error'   }, // vermelho danger
}

const ESTADO_LABEL: Record<EstadoCard, string> = {
  DOCS_OK:    'Documentação completa',
  AGUARDANDO: 'Falta 1 documento',
  PENDENTE:   'Documentos pendentes',
}

/**
 * Campos da análise estruturada antiga (antes da "Análise da Originei" em texto livre).
 * Só leitura no diálogo, quando o detalhe ainda os traz — nunca copiados para o campo novo.
 */
const CAMPOS_LEGADOS: { campo: keyof Oportunidade; rotulo: string; moeda?: boolean }[] = [
  { campo: 'posicionamentoEstrategico', rotulo: 'Posicionamento estratégico' },
  { campo: 'presencaGeografica', rotulo: 'Presença geográfica' },
  { campo: 'receitaLiquidaAnterior', rotulo: 'Receita líquida — ano anterior', moeda: true },
  { campo: 'receitaLiquidaVigente', rotulo: 'Receita líquida — ano vigente', moeda: true },
  { campo: 'resultadoOperacionalAnterior', rotulo: 'Resultado operacional — ano anterior', moeda: true },
  { campo: 'resultadoOperacionalVigente', rotulo: 'Resultado operacional — ano vigente', moeda: true },
  { campo: 'patrimonioLiquido', rotulo: 'Patrimônio líquido', moeda: true },
  { campo: 'endividamento', rotulo: 'Endividamento', moeda: true },
  { campo: 'caixaAtual', rotulo: 'Caixa atual', moeda: true },
  { campo: 'contasReceber', rotulo: 'Contas a receber', moeda: true },
]

const camposLegadosDe = (op: Oportunidade): { rotulo: string; valor: string }[] =>
  CAMPOS_LEGADOS.flatMap(({ campo, rotulo, moeda }) => {
    const v = op[campo]
    if (v == null || v === '') return []
    return [{ rotulo, valor: moeda ? formatBRL(Number(v)) : String(v) }]
  })

// Aderência substituiu a probabilidade digitada (decisão 6.3, 16/08): a fila prioriza a
// reserva mais INSTRUÍDA — documentação, indicadores, andamento — calculada pelo backend
// com o mesmo motor do detalhe. A probabilidade nascia 30 para toda reserva e nunca era
// recalibrada: ordenar por ela era ordenar por nada.
type SortKey = 'ADERENCIA' | 'VALOR' | 'DATA'

type FiltroEtapa = StatusReserva | 'TODAS'

const EstruturacaoPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const usuario = authStore((s) => s.usuario)
  const canEstruturar = useMemo(
    () =>
      !!usuario?.permissoes?.some((p) => {
        const up = p.toUpperCase()
        return up === 'ADMIN' || up === 'GERENTE' || up === 'OPERADOR'
      }),
    [usuario]
  )

  // Guard: consultor/outros caem fora.
  useEffect(() => {
    if (usuario && !canEstruturar) {
      enqueueSnackbar('Acesso restrito a operadores, gerentes e administradores', {
        variant: 'warning',
      })
      navigate('/dashboard')
    }
  }, [usuario, canEstruturar, navigate, enqueueSnackbar])

  const [rows, setRows] = useState<ReservaRow[]>([])
  /** Total da esteira no servidor (pode passar da carga de 200). */
  const [totalServidor, setTotalServidor] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortKey>('ADERENCIA')
  const [filtroEtapa, setFiltroEtapa] = useState<FiltroEtapa>('TODAS')
  // Jornada ADM 30/10, item 4: busca livre + seletores de consultor e produto, no cliente,
  // sobre a mesma carga da esteira (como o filtro de etapa).
  const [busca, setBusca] = useState('')
  const [filtroConsultor, setFiltroConsultor] = useState('')
  const [filtroProduto, setFiltroProduto] = useState('')

  const [docsDialogReserva, setDocsDialogReserva] = useState<ReservaRow | null>(null)
  // Consulta dos documentos (visualizar/baixar por item) — entrega B, 14/10/2026. O envio
  // continua no DocsObrigatoriosDialog, aberto a partir daqui por "Enviar documentos".
  const [documentosReserva, setDocumentosReserva] = useState<ReservaRow | null>(null)
  const [reuniaoDialogReserva, setReuniaoDialogReserva] = useState<ReservaRow | null>(null)
  const [solicitarReuniaoReserva, setSolicitarReuniaoReserva] = useState<ReservaRow | null>(null)

  // ─── Diálogo "Análise da Originei" (entrega H): um campo de texto livre, lido do
  // DETALHE (a listagem não traz o texto) — abrir vazio e salvar apagaria o gravado.
  const [dialogOpen, setDialogOpen] = useState(false)
  const [reservaAtual, setReservaAtual] = useState<ReservaRow | null>(null)
  const [analiseTexto, setAnaliseTexto] = useState('')
  const [analiseSalva, setAnaliseSalva] = useState('')
  const [analiseLegada, setAnaliseLegada] = useState<{ rotulo: string; valor: string }[]>([])
  const [carregandoAnalise, setCarregandoAnalise] = useState(false)
  const [erroAnalise, setErroAnalise] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  /** Abertura mais recente do diálogo — resposta atrasada de outra reserva é descartada. */
  const aberturaRef = useRef(0)

  // ─── Guarda de requisição mais recente (R01 item 3): `carregar` é rechamado por
  // várias ações; uma resposta atrasada não pode sobrescrever a atual. O token
  // também cancela, na desmontagem, qualquer setState tardio.
  const requisicaoRef = useRef(0)
  const montadoRef = useRef(true)
  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
    }
  }, [])

  const carregar = useCallback(async () => {
    if (!canEstruturar) return
    const token = ++requisicaoRef.current
    const atual = () => montadoRef.current && requisicaoRef.current === token
    setLoading(true)
    setErro(null)
    try {
      // Etapa 1: dados base (oportunidades + clientes + documentos).
      const [opsPage, clientesPage, docsPage, catalogo] = await Promise.all([
        oportunidadeService.filtrar(0, TAMANHO_PAGINA, { status: STATUS_ESTEIRA.join(',') }),
        clienteService.listar(0, 200),
        documentoService.listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, undefined, 0, 1000),
        // Catálogo de obrigatórios: só para backend que ainda não manda a contagem na listagem.
        oportunidadeService
          .documentosObrigatoriosCatalogo()
          .catch(() => null),
      ])
      const catalogoDisponivel = catalogo !== null
      const catalogoProdutos: Record<string, DocumentoObrigatorio[]> = catalogo ?? {}

      const listaClientes: Cliente[] = clientesPage.content ?? []
      const clienteById = new Map<string, Cliente>()
      listaClientes.forEach((c) => clienteById.set(c.id, c))

      // Indexa documentos por entidadeReferencia (oportunidadeId).
      type Doc = (typeof docsPage.content)[number]
      const docsPorOp = new Map<string, Doc[]>()
      for (const d of docsPage.content ?? []) {
        const ref = d.entidadeReferencia
        if (!ref) continue
        const arr = docsPorOp.get(ref) ?? []
        arr.push(d)
        docsPorOp.set(ref, arr)
      }

      // Etapa 2: reuniões em bulk (resolve N+1).
      // Tolera falha — cards continuam com a situação da solicitação.
      const oportunidadeIds = (opsPage.content ?? []).map((o) => o.id)
      const reunioesPorOp = await reuniaoService
        .resumoPorOportunidades(oportunidadeIds)
        .catch(() => ({} as Record<string, Reuniao>))

      const novas: ReservaRow[] = (opsPage.content ?? []).map((o) => {
        const cliente = clienteById.get(o.clienteId)
        const atualizadoEm = o.updatedAt ? new Date(o.updatedAt) : null
        const produto = o.tipoNecessidade ?? '—'
        // A listagem já traz a contagem calculada no servidor (a mesma regra que bloqueia o
        // envio). O cálculo local fica só para backend antigo — e indexa o catálogo pelo
        // produtoId, que é a chave do catálogo (antes usava o nome do produto e errava).
        const docsDaOp = docsPorOp.get(o.id) ?? []
        const statusObrig = vincularDocumentos(
          catalogoProdutos[o.produtoId ?? ''] ?? catalogoProdutos[produto] ?? [],
          docsDaOp
        )
        const obrigatoriosFaltantes =
          o.documentosObrigatoriosFaltantes ?? statusObrig.filter((s) => !s.presente).length
        // O total vem do catálogo (a listagem só traz os faltantes). Sem catálogo e sem
        // faltantes, o total é desconhecido — "—", nunca um "Sem checklist" inventado.
        const obrigatoriosTotal: number | null = catalogoDisponivel
          ? Math.max(statusObrig.length, obrigatoriosFaltantes)
          : obrigatoriosFaltantes > 0 ? obrigatoriosFaltantes : null
        const proximaReuniao = reunioesPorOp[o.id] ?? null

        const estado: EstadoCard =
          obrigatoriosFaltantes >= 2 ? 'PENDENTE' : obrigatoriosFaltantes === 1 ? 'AGUARDANDO' : 'DOCS_OK'

        return {
          id: o.id,
          // A reserva traz razão social e CNPJ desde o cadastro em 3 etapas; o cadastro de
          // clientes (que o wizard não alimenta) fica só como reserva para reservas antigas.
          empresa:
            o.razaoSocial || o.nomeFantasia || cliente?.razaoSocial || o.nomeCliente || 'Empresa sem nome',
          cnpj: o.cnpj || cliente?.cnpj ? formatCNPJ(o.cnpj || cliente?.cnpj) : '—',
          consultor: o.consultorNome ?? null,
          produto,
          valor: Number(o.valorEstimado ?? 0),
          aderencia: o.scoreAderencia ?? 0,
          atualizadoEm: atualizadoEm ? atualizadoEm.toLocaleDateString('pt-BR') : '—',
          op: o,
          status: (o.status as StatusReserva | null | undefined) ?? null,
          obrigatoriosFaltantes,
          obrigatoriosTotal,
          proximaReuniao,
          estado,
          reuniaoSolicitadaEm: o.reuniaoSolicitadaEm ?? null,
          reuniaoSolicitadaObservacao: o.reuniaoSolicitadaObservacao ?? null,
          dataPrimeiroContato: o.dataPrimeiroContato ?? null,
          // Campos da listagem (A11): sem N+1. `percentualChecklist` ausente = backend
          // anterior ao contrato → "—" no card, nunca número inventado.
          temDetalhe: o.percentualChecklist != null,
          statusDefesa: o.statusDefesa ?? null,
          checklistPercentual: o.percentualChecklist ?? null,
          parceiroDefinido: o.parceiroSelecionadoId != null,
        }
      })
      if (!atual()) return // resposta obsoleta: outra carga já assumiu
      setRows(novas)
      setTotalServidor(typeof opsPage.totalElements === 'number' ? opsPage.totalElements : null)
      setLoading(false)
    } catch (e) {
      if (!atual()) return
      setErro(e instanceof Error ? e.message : 'Falha ao carregar reservas para estruturação')
      setRows([])
      setTotalServidor(null)
      setLoading(false)
    }
  }, [canEstruturar])

  useEffect(() => {
    carregar()
  }, [carregar])

  // ─── "Na esteira" = total da esteira no servidor (mesma consulta da lista). Os cards
  // antigos (docs pendentes, volume, prontas) saíram com o padrão do Figma (Onda 2 / A05). ───
  const metricas = useMemo(() => ({ total: totalServidor ?? rows.length }), [rows, totalServidor])

  /** Quantas reservas carregadas há em cada etapa — rótulos do filtro. */
  const contagemPorStatus = useMemo(() => {
    const c: Partial<Record<StatusReserva, number>> = {}
    for (const r of rows) if (r.status) c[r.status] = (c[r.status] ?? 0) + 1
    return c
  }, [rows])

  /** Opções dos seletores: o que existe na carga, em ordem alfabética. */
  const opcoesConsultor = useMemo(
    () => [...new Set(rows.map((r) => r.consultor?.trim()).filter((c): c is string => !!c))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rows]
  )
  const opcoesProduto = useMemo(
    () => [...new Set(rows.map((r) => r.produto).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [rows]
  )
  const filtrandoBusca = !!(busca.trim() || filtroConsultor || filtroProduto)
  const limparFiltros = () => {
    setBusca('')
    setFiltroConsultor('')
    setFiltroProduto('')
    setFiltroEtapa('TODAS')
  }

  // ─── Lista filtrada (etapa, busca, consultor, produto) e ordenada conforme o sortBy ───
  const rowsOrdenadas = useMemo(() => {
    const termo = semAcento(busca.trim())
    const arr = rows.filter((r) => {
      if (filtroEtapa !== 'TODAS' && r.status !== filtroEtapa) return false
      if (filtroConsultor && r.consultor?.trim() !== filtroConsultor) return false
      if (filtroProduto && r.produto !== filtroProduto) return false
      if (termo && !semAcento(textoDeBusca(r)).includes(termo)) return false
      return true
    })
    arr.sort((a, b) => {
      if (sortBy === 'ADERENCIA') return b.aderencia - a.aderencia
      if (sortBy === 'VALOR') return b.valor - a.valor
      // DATA — mais recentes primeiro
      const dateA = a.op.updatedAt ?? a.op.createdAt ?? ''
      const dateB = b.op.updatedAt ?? b.op.createdAt ?? ''
      return dateB.localeCompare(dateA)
    })
    return arr
  }, [rows, sortBy, filtroEtapa, busca, filtroConsultor, filtroProduto])

  /** Abre a "Análise da Originei" lendo o texto do detalhe (a listagem não o traz). */
  const abrirAnalise = async (row: ReservaRow) => {
    const abertura = ++aberturaRef.current
    setReservaAtual(row)
    setAnaliseTexto('')
    setAnaliseSalva('')
    setAnaliseLegada([])
    setErroAnalise(null)
    setCarregandoAnalise(true)
    setDialogOpen(true)
    try {
      const op = await oportunidadeService.buscar(row.id)
      if (!montadoRef.current || aberturaRef.current !== abertura) return
      const texto = op?.analiseOriginei ?? ''
      setAnaliseTexto(texto)
      setAnaliseSalva(texto)
      setAnaliseLegada(op ? camposLegadosDe(op) : [])
    } catch (e) {
      if (!montadoRef.current || aberturaRef.current !== abertura) return
      setErroAnalise(mensagemErroDe(e, 'Não foi possível carregar a análise desta reserva'))
    } finally {
      if (montadoRef.current && aberturaRef.current === abertura) setCarregandoAnalise(false)
    }
  }

  // Mantém reservaAtual sincronizada com a lista após recargas.
  useEffect(() => {
    if (!reservaAtual) return
    const atual = rows.find((r) => r.id === reservaAtual.id)
    if (atual && atual !== reservaAtual) setReservaAtual(atual)
  }, [rows, reservaAtual])

  const fechar = () => {
    if (salvando) return
    aberturaRef.current++
    setDialogOpen(false)
    setReservaAtual(null)
  }

  const analiseMudou = analiseTexto.trim() !== analiseSalva.trim()

  /**
   * Salva só o texto da análise. Nunca move a reserva: a etapa é o "Mover para…" do card.
   */
  const salvarAnalise = async () => {
    if (!reservaAtual) return
    setSalvando(true)
    try {
      await oportunidadeService.salvarAnaliseOriginei(reservaAtual.id, analiseTexto.trim() || null)
      enqueueSnackbar('Análise salva', { variant: 'success' })
      aberturaRef.current++
      setDialogOpen(false)
      setReservaAtual(null)
      setSalvando(false)
      await carregar()
    } catch (e: unknown) {
      enqueueSnackbar(mensagemErroDe(e, 'Não foi possível salvar a análise'), { variant: 'error' })
      setSalvando(false)
    }
  }

  /** Depois de mover pelo seletor: recarrega; se a reserva saiu da esteira, avisa onde segue. */
  const aoMover = async (_atualizada: Oportunidade | null, _de: StatusReserva, para: StatusReserva) => {
    await carregar()
    if (!STATUS_ESTEIRA.includes(para)) {
      enqueueSnackbar('A reserva saiu da esteira — acompanhe no Pipeline Comercial', { variant: 'info' })
    }
  }

  if (!canEstruturar) return null

  const carregadas = rows.length

  return (
    <Box>
      <PageHeader
        title="Estruturação Administrativa"
        subtitle="Workspace do back-office: análise, reunião e etapa de cada reserva aprovada"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ViewWeekIcon />}
              onClick={() => navigate('/kanban')}
            >
              Ver Kanban
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={carregar}
              disabled={loading}
            >
              Atualizar
            </Button>
          </Stack>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {/* ─── KPIs do Figma 28:130 (Onda 2 / A05). "Na esteira" é o total da própria
           consulta da lista; os outros três precisam de agregação que o backend não
           fornece (checklist V40 e defesa V41 só existem por reserva) → "—". Contar
           na tela sobre a página carregada seria número inventado (contrato §2). ─── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Na esteira"
            carregando={loading}
            valor={metricas.total}
            legenda="Aprovadas pela Originei até Ativo — mesma fonte da lista"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Documentação < 50%"
            valor="—"
            legenda="Sem fonte agregada no backend"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Defesa em preparação"
            valor="—"
            legenda="Sem fonte agregada no backend"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Aguardando parceiro"
            valor="—"
            legenda="Sem fonte agregada no backend"
          />
        </Grid>
      </Grid>

      {/* ─── Lista de cards das reservas ─── */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Esteira de estruturação — reservas aprovadas pela Originei
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {/* Filtro por etapa: no cliente, sobre a carga da esteira (os KPIs não filtram). */}
            <TextField
              select
              size="small"
              label="Etapa"
              value={filtroEtapa}
              onChange={(e) => setFiltroEtapa(e.target.value as FiltroEtapa)}
              sx={{ minWidth: 240 }}
              inputProps={{ 'aria-label': 'Filtrar por etapa' }}
            >
              <MenuItem value="TODAS">{`Todas as etapas (${carregadas})`}</MenuItem>
              {STATUS_ESTEIRA.map((s) => (
                <MenuItem key={s} value={s}>
                  {`${STATUS_LABEL[s]} (${contagemPorStatus[s] ?? 0})`}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Ordenar por:
            </Typography>
            <TextField
              select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortKey)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="ADERENCIA">Aderência (maior primeiro)</MenuItem>
              <MenuItem value="VALOR">Valor (maior primeiro)</MenuItem>
              <MenuItem value="DATA">Data (mais recentes)</MenuItem>
            </TextField>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          sx={{ mb: 2 }}
          role="search"
          aria-label="Filtros da esteira"
        >
          <TextField
            size="small"
            label="Buscar reserva"
            placeholder="Nº da reserva, empresa, CNPJ ou consultor"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={{ minWidth: 300 }}
          />
          <TextField
            select
            size="small"
            label="Consultor"
            value={filtroConsultor}
            onChange={(e) => setFiltroConsultor(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {opcoesConsultor.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Produto"
            value={filtroProduto}
            onChange={(e) => setFiltroProduto(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Todos</MenuItem>
            {opcoesProduto.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          {(filtrandoBusca || filtroEtapa !== 'TODAS') && (
            <>
              <Button size="small" onClick={limparFiltros}>
                Limpar filtros
              </Button>
              <Typography variant="caption" color="text.secondary">
                {rowsOrdenadas.length} de {carregadas} reservas
              </Typography>
            </>
          )}
        </Stack>

        {!loading && totalServidor != null && totalServidor > carregadas && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Mostrando {carregadas} de {totalServidor} reservas da esteira (carga limitada a {TAMANHO_PAGINA}).
            Os filtros atuam sobre as reservas carregadas.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rowsOrdenadas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            {filtrandoBusca
              ? 'Nenhuma reserva com esses filtros.'
              : filtroEtapa === 'TODAS'
                ? 'Nenhuma reserva aprovada na esteira. Reservas aguardando aprovação ficam em Validação de Reservas.'
                : `Nenhuma reserva em ${STATUS_LABEL[filtroEtapa]}.`}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {rowsOrdenadas.map((r) => {
              const cor = ESTADO_COLOR[r.estado]
              const reuniao = situacaoReuniao(r)
              const reuniaoRealizada = !!r.dataPrimeiroContato
              const semConsultor = !r.op.usuarioResponsavel
              const rotuloSolicitar = r.reuniaoSolicitadaEm ? 'Solicitar novamente' : 'Solicitar reunião'
              const solicitacaoPendente = !!r.reuniaoSolicitadaEm && !reuniaoRealizada
              return (
                <Box
                  key={r.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `4px solid ${cor.border}`,
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  {/* Linha 1: empresa + acoes */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {r.empresa}
                        </Typography>
                        {r.status && (
                          <Chip
                            label={STATUS_LABEL[r.status]}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6875rem',
                              bgcolor: corDoStatus(r.status),
                              color: '#fff',
                            }}
                          />
                        )}
                        <Chip
                          label={ESTADO_LABEL[r.estado]}
                          size="small"
                          color={cor.chip}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.6875rem' }}
                        />
                        {solicitacaoPendente && (
                          <Tooltip
                            title={`Solicitada em ${formatData(r.reuniaoSolicitadaEm as string)}${
                              r.reuniaoSolicitadaObservacao ? ` — ${r.reuniaoSolicitadaObservacao}` : ''
                            }`}
                          >
                            <Chip
                              label="Reunião solicitada"
                              size="small"
                              color="info"
                              icon={<EventIcon />}
                              sx={{ height: 20, fontSize: '0.6875rem' }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {r.cnpj} · Consultor: {r.consultor ?? '—'} · {r.produto} · {formatBRL(r.valor)} · Aderência {r.aderencia}/100
                        {' · '}Documentação{' '}
                        {r.checklistPercentual != null ? `${r.checklistPercentual}%` : '—'}
                        {' · '}Defesa:{' '}
                        {r.statusDefesa
                          ? STATUS_DEFESA_LABEL[r.statusDefesa]
                          : r.temDetalhe ? 'Aguardando' : '—'}
                      </Typography>
                    </Box>
                    {/* ─── Ações do card: a etapa é o "Mover para…" (máquina de status) ─── */}
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                      {r.status && (
                        <SeletorEtapaReserva
                          reservaId={r.id}
                          empresa={r.empresa}
                          status={r.status}
                          versao={r.op.versao ?? null}
                          usuarioId={usuario?.id ?? ''}
                          disabled={!usuario?.id}
                          onMovida={aoMover}
                          onConflito={carregar}
                        />
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpenIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setDocumentosReserva(r)}
                        color={
                          r.obrigatoriosFaltantes > 0
                            ? r.estado === 'PENDENTE' ? 'error' : 'warning'
                            : 'inherit'
                        }
                      >
                        Documentos
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AssessmentIcon sx={{ fontSize: 14 }} />}
                        onClick={() => void abrirAnalise(r)}
                      >
                        Análise
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                        onClick={() => navigate(`/reservas/${r.id}/defesa`)}
                      >
                        Defesa
                      </Button>
                      {/* Pedir a reunião ao consultor (entrega G): avisa e destaca em Minhas
                          Reservas. Repetível; sem sentido depois do primeiro contato. */}
                      <Tooltip
                        title={
                          reuniaoRealizada
                            ? 'Reunião já realizada'
                            : semConsultor
                              ? 'Reserva sem consultor responsável'
                              : 'Pedir ao consultor a reunião com o cliente'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EventIcon sx={{ fontSize: 14 }} />}
                            aria-label={`${rotuloSolicitar} — ${r.empresa}`}
                            onClick={() => setSolicitarReuniaoReserva(r)}
                            disabled={reuniaoRealizada || semConsultor}
                          >
                            {rotuloSolicitar}
                          </Button>
                        </span>
                      </Tooltip>
                      {/* Reunião só depois da aprovação da Originei (06/09) — na fila de
                          estruturação isso é sempre verdade; a trava fica por fail-closed. */}
                      <Tooltip
                        title={
                          permiteAgendamento(r.status ?? undefined)
                            ? 'Agendar reunião'
                            : MOTIVO_AGENDAMENTO_INDISPONIVEL
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setReuniaoDialogReserva(r)}
                            disabled={!permiteAgendamento(r.status ?? undefined)}
                          >
                            Agendar
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>

                  {/* Linha 2: Documentos, Reunião, Atualizada */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, fontSize: '0.75rem' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Documentos: </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight={700}
                        color={
                          r.obrigatoriosTotal === null || r.obrigatoriosTotal === 0
                            ? 'text.secondary'
                            : r.obrigatoriosFaltantes === 0
                              ? 'success.main'
                              : r.estado === 'PENDENTE' ? 'error.main' : 'warning.main'
                        }
                      >
                        {r.obrigatoriosTotal === null
                          ? '—'
                          : r.obrigatoriosTotal === 0
                          ? 'Sem checklist'
                          : `${r.obrigatoriosTotal - r.obrigatoriosFaltantes} / ${r.obrigatoriosTotal} OK`}
                      </Typography>
                    </Box>
                    {/* REUNIÃO — realizada / agendada / solicitada ao consultor */}
                    <Box>
                      <Typography variant="caption" color="text.secondary">Reunião: </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        fontWeight={reuniao.destaque ? 700 : 400}
                        color={reuniao.destaque ? 'text.primary' : 'text.secondary'}
                      >
                        {reuniao.texto}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Atualizada: </Typography>
                      <Typography component="span" variant="caption">{r.atualizadoEm}</Typography>
                    </Box>
                  </Box>

                  {/* Banner de pendência documental (>= 2 obrigatórios faltando) — só sinaliza */}
                  {r.estado === 'PENDENTE' && (
                    <Box
                      sx={{
                        mt: 1.5,
                        py: 1,
                        px: 1.5,
                        bgcolor: 'rgba(231,76,60,0.08)',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                      }}
                    >
                      <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        Pendência: faltam <strong>{r.obrigatoriosFaltantes}</strong> documentos obrigatórios.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Stack>
        )}
      </Paper>

      {/* ─── MODAL: Análise da Originei (texto livre, uso interno) ─── */}
      {/* Clique fora/Esc com texto não salvo não fecha: até 20 mil caracteres se perderiam. */}
      <Dialog open={dialogOpen} onClose={() => !analiseMudou && fechar()} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
          Análise da Originei
          {reservaAtual && (
            <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {reservaAtual.empresa} · CNPJ {reservaAtual.cnpj} · {reservaAtual.produto}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {carregandoAnalise ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} aria-label="Carregando análise" />
              </Box>
            ) : erroAnalise ? (
              <Alert severity="error">{erroAnalise}</Alert>
            ) : (
              <>
                <TextField
                  label="Análise da Originei"
                  value={analiseTexto}
                  onChange={(e) => setAnaliseTexto(e.target.value)}
                  multiline
                  minRows={10}
                  fullWidth
                  autoFocus
                  inputProps={{ maxLength: ANALISE_MAX }}
                  helperText={`Uso interno — o consultor não vê. Este texto também vai como contexto para a IA na Defesa de crédito. · ${analiseTexto.length}/${ANALISE_MAX}`}
                  placeholder="Leitura da operação, posicionamento, presença geográfica, números relevantes, riscos e mitigantes…"
                />
                {analiseLegada.length > 0 && (
                  <Accordion disableGutters variant="outlined">
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2" fontWeight={600}>
                        Análise anterior (campos estruturados)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {analiseLegada.map((c) => (
                          <Box key={c.rotulo}>
                            <Typography variant="caption" color="text.secondary">{c.rotulo}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.valor}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            startIcon={<AssignmentIcon />}
            onClick={() => reservaAtual && navigate(`/reservas/${reservaAtual.id}/defesa`)}
            disabled={salvando || !reservaAtual}
          >
            Defesa de crédito
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={fechar} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={salvando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={salvarAnalise}
            disabled={salvando || carregandoAnalise || !!erroAnalise || !analiseMudou}
          >
            {salvando ? 'Salvando…' : 'Salvar análise'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documentos da reserva: checklist com os arquivos por item, visualizar e baixar */}
      <DocumentosReservaDialog
        open={!!documentosReserva}
        reservaId={documentosReserva?.id ?? null}
        titulo={documentosReserva ? `${documentosReserva.empresa} · ${documentosReserva.cnpj}` : ''}
        onClose={() => setDocumentosReserva(null)}
        onEnviar={() => {
          setDocsDialogReserva(documentosReserva)
          setDocumentosReserva(null)
        }}
      />

      {/* Dialog de docs obrigatorios */}
      {docsDialogReserva && (
        <DocsObrigatoriosDialog
          open={!!docsDialogReserva}
          onClose={() => setDocsDialogReserva(null)}
          reservaId={docsDialogReserva.id}
          produto={docsDialogReserva.produto}
          empresa={docsDialogReserva.empresa}
          onUploadSucesso={carregar}
        />
      )}

      {/* Dialog de agendamento de reuniao */}
      {reuniaoDialogReserva && (
        <ReuniaoDialog
          open={!!reuniaoDialogReserva}
          onClose={() => setReuniaoDialogReserva(null)}
          prefill={{
            oportunidadeId: reuniaoDialogReserva.id,
            clienteId: reuniaoDialogReserva.op.clienteId,
            clienteNome: reuniaoDialogReserva.empresa,
            titulo: `Estruturação — ${reuniaoDialogReserva.empresa}`,
            // Primeira reunião enquanto não houver primeiro contato: realizá-la carimba o
            // contato e encerra o pedido feito ao consultor (revisão de 16/10/2026).
            primeiroContato: !reuniaoDialogReserva.dataPrimeiroContato,
          }}
          onSaved={() => {
            setReuniaoDialogReserva(null)
            carregar()
          }}
        />
      )}

      {/* Pedido de reunião ao consultor (entrega G) */}
      {solicitarReuniaoReserva && (
        <SolicitarReuniaoDialog
          open={!!solicitarReuniaoReserva}
          empresa={solicitarReuniaoReserva.empresa}
          consultor={solicitarReuniaoReserva.consultor}
          reservaId={solicitarReuniaoReserva.id}
          jaSolicitada={!!solicitarReuniaoReserva.reuniaoSolicitadaEm}
          solicitadaEm={solicitarReuniaoReserva.reuniaoSolicitadaEm}
          onClose={() => setSolicitarReuniaoReserva(null)}
          onSolicitada={async () => {
            setSolicitarReuniaoReserva(null)
            await carregar()
          }}
          onCancelada={async () => {
            setSolicitarReuniaoReserva(null)
            await carregar()
          }}
        />
      )}
    </Box>
  )
}

export default EstruturacaoPage
